import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '../assets/icons/search.svg';
import { setOrders, setLoading, setError } from '../store/orderSlice';
import { fetchOrders } from '../services/order';
import Pagination from './Pagination';

export default function OrderHistoryCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders, counts, pagination, loading } = useSelector(
    (state) => state.order
  );
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const loadOrders = async () => {
      try {
        dispatch(setLoading(true));
        let status = null;

        if (activeTab === 'approve') {
          status = 'Awaiting Approval';
        } else if (activeTab === 'ship') {
          status = ['Preparing for shipment', 'Order is shipped'];
        } else if (activeTab === 'completed') {
          status = 'Delivered';
        }

        const searchTerm = searchQuery.trim() || null;
        
        // Fetch orders with status filter (for current tab)
        const data = await fetchOrders(status, currentPage, searchTerm);
        
        // Fetch counts without status filter (for badges) but with search if searching
        const countsData = await fetchOrders(null, 1, searchTerm);
        
        if (!cancelled) {
          // Use orders from status-filtered call, but counts from global call
          dispatch(setOrders({ 
            ...data, 
            counts: countsData.counts || {},
            currentPage 
          }));
        }
      } catch (error) {
        if (!cancelled) {
          dispatch(setError(error.message || 'Failed to load orders'));
        }
      }
    };
    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [activeTab, currentPage, searchQuery, dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  const transformedOrders = useMemo(() => {
    return orders.map((order) => ({
      id: order.order_id,
      customer: order.customer_name || 'N/A',
      supplements: order.product_count,
      price: `€${parseFloat(order.price).toFixed(2)}`,
      approved: order.is_approved,
      added: order.ordered_date
        ? new Date(order.ordered_date)
            .toLocaleDateString('en-GB')
            .replace(/\//g, '.')
        : 'N/A',
      ordered: order.created_at
        ? new Date(order.created_at)
            .toLocaleDateString('en-GB')
            .replace(/\//g, '.')
        : 'N/A',
      status: order.shipment_status,
    }));
  }, [orders]);

  const badgeCounts = useMemo(() => {
    const awaitingApproval = counts['Awaiting Approval'] || 0;
    const orderIsShipped = counts['Order is shipped'] || 0;
    const preparingForShipment = counts['Preparing for shipment'] || 0;
    const delivered = counts['Delivered'] || 0;

    return {
      all: awaitingApproval + orderIsShipped + preparingForShipment + delivered,
      approve: awaitingApproval,
      ship: preparingForShipment + orderIsShipped,
      completed: delivered,
    };
  }, [counts]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="inline-flex flex-wrap gap-2 bg-white rounded-lg p-1.5 shadow-sm">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg font-poppins text-sm transition-all ${
              activeTab === 'all'
                ? 'bg-[#E7F6EF] text-[#119975]'
                : 'bg-transparent text-gray-500 hover:bg-gray-50'
            }`}
          >
            All Orders{' '}
            <span
              className={`inline-flex items-center justify-center ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                activeTab === 'all'
                  ? 'bg-[#119975] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {badgeCounts.all}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('approve')}
            className={`px-4 py-2 rounded-lg font-poppins text-sm transition-all ${
              activeTab === 'approve'
                ? 'bg-[#E7F6EF] text-[#119975]'
                : 'bg-transparent text-gray-500 hover:bg-gray-50'
            }`}
          >
            Orders to Approve{' '}
            <span
              className={`inline-flex items-center justify-center ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                activeTab === 'approve'
                  ? 'bg-[#0D7A5E] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {badgeCounts.approve}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('ship')}
            className={`px-4 py-2 rounded-lg font-poppins text-sm transition-all ${
              activeTab === 'ship'
                ? 'bg-[#E7F6EF] text-[#119975]'
                : 'bg-transparent text-gray-500 hover:bg-gray-50'
            }`}
          >
            Orders to Ship{' '}
            <span
              className={`inline-flex items-center justify-center ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                activeTab === 'ship'
                  ? 'bg-[#0D7A5E] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {badgeCounts.ship}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 rounded-lg font-poppins text-sm transition-all ${
              activeTab === 'completed'
                ? 'bg-[#E7F6EF] text-[#119975]'
                : 'bg-transparent text-gray-500 hover:bg-gray-50'
            }`}
          >
            Completed{' '}
            <span
              className={`inline-flex items-center justify-center ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                activeTab === 'completed'
                  ? 'bg-[#0D7A5E] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {badgeCounts.completed}
            </span>
          </button>
        </div>

        <div className="relative">
          <img
            src={SearchIcon}
            alt="Search"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#119975] focus:border-transparent font-poppins text-sm w-full md:w-64"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-10 w-10 border-4 border-[#C1DCD4] border-t-[#119975] rounded-full" />
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#E8E8E8]">
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Order ID
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Customer
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Supplements
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Price
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Approval
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Added
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Ordered
                    </th>
                    <th className="px-6 py-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                      Shipment Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transformedOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-6 py-12 text-center text-gray-500 font-poppins"
                      >
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    transformedOrders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => navigate(`/order-details/${order.id}`)}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-6 whitespace-nowrap text-sm font-medium font-poppins">
                          <span className="text-[#119975]">{order.id}</span>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-700 font-poppins">
                          {order.customer}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-700 font-poppins">
                          {order.supplements}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-700 font-poppins">
                          {order.price}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          {order.approved ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-[#E7F6EF] text-[#119975] font-poppins">
                              Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 font-poppins">
                              Awaiting Approval
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-700 font-poppins">
                          {order.added}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-700 font-poppins">
                          {order.ordered}
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium font-poppins ${
                              order.status === 'Delivered'
                                ? 'bg-[#E7F6EF] text-[#119975]'
                                : order.status === 'Order is shipped' ||
                                    order.status === 'Preparing for shipment'
                                  ? 'bg-[#FFF4E6] text-[#FF8A3C]'
                                  : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-gray-200">
              {transformedOrders.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 font-poppins">
                  No orders found
                </div>
              ) : (
                transformedOrders.map((order) => (
                  <div
                    key={order.id}
                    onClick={() => navigate(`/order-details/${order.id}`)}
                    className="p-4 space-y-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold font-poppins text-[#119975]">
                        Order #{order.id}
                      </span>
                      {order.approved ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-[#E7F6EF] text-[#119975] font-poppins">
                          Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 font-poppins">
                          Awaiting Approval
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-poppins">
                          Customer:
                        </span>
                        <span className="text-gray-900 font-poppins">
                          {order.customer}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-poppins">
                          Supplements:
                        </span>
                        <span className="text-gray-900 font-poppins">
                          {order.supplements}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-poppins">
                          Price:
                        </span>
                        <span className="text-gray-900 font-poppins">
                          {order.price}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-poppins">
                          Added:
                        </span>
                        <span className="text-gray-900 font-poppins">
                          {order.added}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-poppins">
                          Ordered:
                        </span>
                        <span className="text-gray-900 font-poppins">
                          {order.ordered}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-poppins">
                          Status:
                        </span>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium font-poppins ${
                            order.status === 'Delivered'
                              ? 'bg-[#E7F6EF] text-[#119975]'
                              : order.status === 'Order is shipped' ||
                                  order.status === 'Preparing for shipment'
                                ? 'bg-[#FFF4E6] text-[#FF8A3C]'
                                : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {!loading && transformedOrders.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          hasNext={pagination.hasNext}
          hasPrevious={pagination.hasPrevious}
          onPageChange={setCurrentPage}
          totalCount={pagination.count}
          itemsPerPage={10}
        />
      )}
    </div>
  );
}
